import { KeyValue } from "@angular/common";
import { DateTime } from "luxon";
import structuredClone from "@ungap/structured-clone";
import { isNumber, isUndefined } from "lodash";
import { environment } from "environments/environment";
import _ from "lodash";

// Preserve original property order
export const originalOrder = (a: KeyValue<number, string>, b: KeyValue<number, string>): number => 0;

// Order by ascending property value
export const valueAscOrder = (a: KeyValue<number, string>, b: KeyValue<number, string>): number => a.value.localeCompare(b.value);

// Order by descending property key
export const keyDescOrder = (a: KeyValue<number, string>, b: KeyValue<number, string>): number => a.key > b.key ? -1 : (b.key > a.key ? 1 : 0);

export const isTransient = (input: any): boolean => !input || !Object.hasOwn(input, 'id') || !input.id || input.id === 'new';

export const has = (input: any, propertyName: string): boolean => input && Object.hasOwn(input, propertyName);

export const isObject = (input: unknown): input is Record<string, any> => {
    return typeof input === 'object' && input !== null && !Array.isArray(input);
};

export const isDefined = (input: any): boolean => (typeof input !== 'undefined' && input !== null);

export const hasValue = (input: any): boolean => {
    if (!isDefined(input)) {
        return false;
    }

    if (Array.isArray(input)) {
        return input.length > 0;
    }

    if (typeof input == 'object') {
        return Object.keys(input).length > 0;
    }

    if (typeof input == 'string') {
        return input !== '';
    }

    if (typeof input == 'number') {
        return true;
    }

    //TODO: other tests and checks as required here...

    return !!input; // truthy/falsy
}

/**
 * Check if the object has a built-in symbol representing the iterator method.
 * @param obj the object to test.
 * @returns A flag indicating if the object is Iterable.
 */
export const isIterable = (obj: any): boolean => {
    return obj != null && typeof obj[Symbol.iterator] === 'function';
}

/**
 *
 * @param input: string - the input string to be camelCased.
 * @returns a camel case version of the input string.
 */
export const camelCase = (input: string): string =>
    input.length > 0
        ? input.charAt(0).toLowerCase() + input.slice(1)
        : input;

/**
 *
 * @param input: string - the input string to be PascalCased.
 * @returns A pascal case version of the input string.
 */
export const pascalCase = (input: string): string =>
    input.length > 0
        ? input.charAt(0).toUpperCase() + input.slice(1)
        : input;

/**
 *
 * @param args an array of values to be turned into a url-safe slug string.
 * @returns a url-safe version of the input represented as a "slug".
 */
export const slugify = (...args: (string | number)[]): string => {
    const value = args.join(' ')
    if (value.length === 0) {
        return '';
    }

    const separator = '_';
    let result = value
        .normalize('NFD') // split an accented letter in the base letter and the accent
        .replace(/[\u0300-\u036f]/g, '') // remove all previously split accents
        .toLowerCase()
        .trim()
        .replace(/\s+/g, separator)     // Replace spaces with '_'
        .replace(/\-/g, separator)      // Replace '-' with '_'
        .replace(/[^\w]+/g, separator)  // Remove all non-word chars
        .replace(/__+/g, separator)     // Replace multiple '_' with single '_'
        .replace(/(^_|_$)/g, '');       // Remove trailing '_'

    result = result.charAt(0).toUpperCase() + result.slice(1);
    return result;
}

export const uuid = (): string => {
    return self.crypto.randomUUID();
}

export const escapeRegEx = (input: string): string => {
    // $& means the whole matched string
    return input.replace(/[.*+?^${}()|[\]\\\/-]/g, '\\$&');
}

export const tryGetProp = (data: any, propertyName: string): any => {
    if (!data || !propertyName) {
        return undefined;
    }

    var names = [propertyName, camelCase(propertyName), pascalCase(propertyName)];
    for (var i = 0; i < names.length; i++) {
        if (Object.hasOwn(data, names[i])) {
            return data[names[i]];
        }
    }

    return undefined;
}

/**
 * Sets a property of "obj" from a value in "data" if the property exists in "data.
 * Otherwise, it leaves "obj" as is.
 * @param obj The input object to be updated.
 * @param data The data source to set the data from.
 * @param propertyName The property name to copy from the "data" to the "obj".
 * @returns A boolean flag indicating if the data copy was successful.
 */
export const trySetProp = (obj: any, data: any, propertyName: string, forceCamelCasePropertyName: boolean = true): boolean => {
    if (!obj || !data || !propertyName) {
        return false;
    }

    if (Object.hasOwn(data, propertyName) && data[propertyName] != null) {
        let localPropName = propertyName;
        if (forceCamelCasePropertyName) {
            localPropName = camelCase(propertyName);
        }
        obj[localPropName] = data[propertyName];
        return true;
    }

    return false;
}

export const tryParseDate = (input: any): DateTime => {
    if (input) {
        if (typeof input === 'object' && Object.hasOwn(input, '$date')) {
            return DateTime.fromMillis(input['$date']);
        }

        if (typeof input == 'string' && /^\d{2,4}([\/\\\-]\d{2}){2}/i.test(input)) {
            let d = DateTime.fromISO(input);
            if (d.isValid) {
                return d;
            }
        }
    }

    return undefined;
}

export const deepClone = (obj): any => {
    // https://developer.mozilla.org/en-US/docs/Web/API/structuredClone#browser_compatibility
    if (!globalThis.structuredClone) {
        globalThis.structuredClone = structuredClone;
    }

    return globalThis.structuredClone(obj);
}

/**
 * @ngdoc function
 * @name angular.toJson
 * @module ng
 * @kind function
 *
 * @description
 * Serializes input into a JSON-formatted string. Properties with leading $$ characters will be
 * stripped since AngularJS uses this notation internally.
 *
 * @param {Object|Array|Date|string|number|boolean} obj Input to be serialized into JSON.
 * @param {boolean|number} [pretty=2] If set to true, the JSON output will contain newlines and whitespace.
 *    If set to an integer, the JSON output will contain that many spaces per indentation.
 * @returns {string|undefined} JSON-ified string representing `obj`.
 * @knownIssue
 *
 * The Safari browser throws a `RangeError` instead of returning `null` when it tries to stringify a `Date`
 * object with an invalid date value. The only reliable way to prevent this is to monkeypatch the
 * `Date.prototype.toJSON` method as follows:
 *
 * ```
 * var _DatetoJSON = Date.prototype.toJSON;
 * Date.prototype.toJSON = function() {
 *   try {
 *     return _DatetoJSON.call(this);
 *   } catch(e) {
 *     if (e instanceof RangeError) {
 *       return null;
 *     }
 *     throw e;
 *   }
 * };
 * ```
 *
 * See https://github.com/angular/angular.js/pull/14221 for more information.
 */
export const toJson = (obj: any, pretty: number|boolean = false): string => {
    if (isUndefined(obj)) return undefined;

    let prettyPrint = null;
    if (!isNumber(pretty)) {
        prettyPrint = pretty ? 2 : null;
    }

    return JSON.stringify(obj, toJsonReplacer, prettyPrint);
}

const toJsonReplacer = (key: string, value: any) => {
    let val = value;

    if (typeof key === 'string' && key.startsWith('$$')) {
        val = undefined;
    } else if (isWindow(value)) {
        val = '$WINDOW';
    } else if (value &&  window.document === value) {
        val = '$DOCUMENT';
    }

    return val;
}

/**
 * Checks if `obj` is a window object.
 *
 * @private
 * @param {*} obj Object to check
 * @returns {boolean} True if `obj` is a window obj.
 */
const isWindow = (obj: any) => {
    return obj && obj.window === obj;
}

export const fileUrl = (input: string, queryData: any, relativeUrl?: string) => {
    if (input !== undefined && input !== null) {
        queryData.fileId = input;
    }

    let basePath = '';
    let fileUploadEndpoint = environment.config.remoteServiceBaseUrl + environment.config.fileUploadEndpoint;
    return (relativeUrl ? '' : basePath) + fileUploadEndpoint + queryStringBuilder(queryData);
};

export const queryStringBuilder = (queryData) => {
    var queryDataCopy = _.clone(queryData);
    for (var prop in queryDataCopy) {
        if (Object.hasOwn(queryDataCopy, prop)) {
            queryDataCopy[prop] = encodeURIComponent(queryDataCopy[prop]);
        }
    }

    return JSON.stringify(queryDataCopy)
        .split('":"')
        .join('=')
        .split('","')
        .join('&')
        .replace('{"', '?')
        .replace('"}', '');
};

export const isElementVisible = (el: HTMLElement): boolean => {
    const style = getComputedStyle(el);
    return style.display !== 'none' && style.visibility !== 'hidden' && el.offsetParent !== null;
}

export const trimTrailingSlash = (input: string): string => input.replace(/\/+$/, '');
